import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, RotateCcw, Search, Send, UserPlus } from 'lucide-react';
import { roleNeedsWarehouse, type Scope } from '@eombor/contracts';
import { Badge, Button, Dialog, Input, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { FieldAppearanceContext } from '../../../shared/ui/field-appearance.js';
import { ScopePicker, scopeForRole, type ScopeOption } from '../../rbac/ui/ScopePicker.js';
import { useCopyToClipboard } from '../../../shared/clipboard/useCopyToClipboard.js';
import { subdivisionApi } from '../../subdivision/api/subdivision.api.js';
import type { RoleDto } from '../../rbac/api/rbac.api.js';
import { workplaceUnitsOf } from './WorkplaceDialog.js';
import { PositionField, EMPTY_POSITION, type PositionValue } from './PositionField.js';
import {
  staffApi,
  type CreateStaffResultDto,
  type PersonnelLookupDto,
} from '../api/staff.api.js';

/**
 * XODIM QO'SHISH — JSHSHIR orqali.
 *
 * Uch qadam va tartibi ataylab shunday:
 *   1. JSHSHIR teriladi va xodim KADRLAR BAZASIDAN topiladi;
 *   2. topilgani ekranda ko'rsatiladi, administrator faqat rol va doira
 *      tanlaydi;
 *   3. yaratilgan parol ekranda yoki Telegramda yetkaziladi.
 *
 * F.I.Sh, korxona va lavozim maydonlari YO'Q: ular kadrlar bazasidan keladi.
 * Qo'lda terishga ruxsat berilsa, bir xil odam ikki xil ism bilan ikki marta
 * qo'shilardi va keyin qaysi biri haqiqiy ekani hech qachon aniqlanmasdi.
 */

const JSHSHIR_LENGTH = 14;

export interface AddStaffDialogProps {
  open: boolean;
  onClose: () => void;
  /** Rol ro'yxati — sahifada allaqachon yuklangan bo'ladi. */
  roles: RoleDto[];
  scopeOptions: ScopeOption[];
  /** Muvaffaqiyatli qo'shilgach ro'yxatni yangilash uchun. */
  onCreated: () => void;
}

const errorText = (error: unknown, fallback: string): string =>
  error instanceof ApiClientError ? error.message : fallback;

export const AddStaffDialog = ({
  open,
  onClose,
  roles,
  scopeOptions,
  onCreated,
}: AddStaffDialogProps) => {
  const jshshirId = useId();
  const [jshshir, setJshshir] = useState('');
  const [found, setFound] = useState<PersonnelLookupDto | null>(null);
  /* QO'LDA KIRITISH — kadrlar bazasida umuman yo'q xodim uchun. Ataylab
     yoqiladi: xato terilgan JSHSHIR jimgina yangi odam yaratib
     yubormasligi kerak. */
  const [manual, setManual] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState('');
  const [scope, setScope] = useState<Scope>({ type: 'ORG_UNIT', id: null });
  /* Ish joyi — kadrlar bazasida u bo'lmaganda administrator ko'rsatadi. */
  const [orgUnitId, setOrgUnitId] = useState('');
  const [position, setPosition] = useState<PositionValue>(EMPTY_POSITION);
  /* Bo'linma — korxona ichidagi bo'lim. IXTIYORIY: biriktirilmagan xodim
     ham talabnoma yozaveradi, shunchaki uning bo'linma ombori shakllanmaydi. */
  const [subdivisionId, setSubdivisionId] = useState('');
  const [result, setResult] = useState<CreateStaffResultDto | null>(null);
  const [copied, setCopied] = useState(false);
  const { copy: copyText } = useCopyToClipboard();

  const lookup = useMutation({
    mutationFn: (value: string) => staffApi.lookup(value),
    onSuccess: (data) => {
      setFound(data);
      /*
       * Doira xodimning O'Z korxonasiga oldindan qo'yiladi — bu 95% holatda
       * to'g'ri javob va administrator uni qayta terib o'tirmaydi. Korxonasi
       * yo'q xodim uchun tanlov bo'sh qoladi: taxmin qilinsa, rol butunlay
       * boshqa joyga berilib ketardi.
       */
      setScope({ type: 'ORG_UNIT', id: data.orgUnitId });
      setOrgUnitId('');
      setPosition(EMPTY_POSITION);
      setSubdivisionId('');
    },
  });

  /*
   * Lavozimlar FAQAT kerak bo'lganda so'raladi.
   *
   * Kadrlar bazasida ish joyi bo'lgan xodim uchun bu ro'yxat ochilmaydi
   * ham: 200 ta yozuvni har oyna ochilganda tortib olish, uni hech kim
   * ko'rmasa ham, bekorga.
   */
  /*
   * Bo'linmalar xodimning ISH JOYIGA qarab tortiladi.
   *
   * Korxona ma'lum bo'lgandagina so'raladi: ish joyi kadrlar bazasidan
   * kelsa — o'sha zahoti, administrator o'zi tanlasa — u tanlagandan keyin.
   * Korxona noma'lum bo'lganda ro'yxatning ma'nosi ham yo'q: «R va AX» qaysi
   * korxonaniki ekani aniqlanmagan bo'lardi.
   */
  const workplaceOrgUnitId = found?.orgUnitId ?? (orgUnitId === '' ? null : orgUnitId);

  const subdivisions = useQuery({
    queryKey: ['subdivisions', 'for-staff', workplaceOrgUnitId ?? ''],
    queryFn: () =>
      subdivisionApi.list({ orgUnitId: workplaceOrgUnitId ?? '', activeOnly: true }),
    enabled: open && workplaceOrgUnitId !== null,
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      staffApi.create({
        jshshir: manual ? digits : (found?.jshshir ?? ''),
        roleId,
        scope,
        /* Qo'lda kiritilgan xodim — ismi va telefoni so'rov bilan ketadi;
           server ularni `personnel` ga yozadi va `is_manual` bilan
           belgilaydi. Kadrlar bazasida BOR odam uchun yuborilmaydi —
           server uni rad etadi va to'g'ri qiladi. */
        ...(manual
          ? {
              fullName: fullName.trim(),
              ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
            }
          : {}),
        /* Kadrlar bazasida ish joyi BOR bo'lsa yuborilmaydi — server
           uni rad etadi va to'g'ri qiladi. */
        /* Lavozim yo ro'yxatdan (`positionId`), yo qo'lda yozilgan nom
           (`positionName`) bilan ketadi — ikkalasi birga emas. */
        ...(needsWorkplace
          ? {
              orgUnitId,
              ...(position.name === null
                ? { positionId: position.id ?? '' }
                : { positionName: position.name }),
            }
          : {}),
        /* Bo'sh tanlov umuman YUBORILMAYDI: server `undefined` ni «bo'lim
           yo'q» deb o'qiydi, bo'sh satrni esa noto'g'ri identifikator deb. */
        ...(subdivisionId === '' ? {} : { subdivisionId }),
      }),
    onSuccess: (data) => {
      setResult(data);
      onCreated();
    },
  });

  // Har ochilishda toza boshlanadi — oldingi xodimning paroli qolib ketmasin.
  useEffect(() => {
    if (!open) return;
    setJshshir('');
    setFound(null);
    setManual(false);
    setFullName('');
    setPhone('');
    setRoleId('');
    setScope({ type: 'ORG_UNIT', id: null });
    setOrgUnitId('');
    setPosition(EMPTY_POSITION);
    setResult(null);
    setCopied(false);
    lookup.reset();
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutatsiyalar har chizishda yangi
  }, [open]);

  const role = roles.find((item) => item.id === roleId) ?? null;
  /* Ombor roli tanlangan zahoti doira "Ombor" ga o'tadi va tanlagichda
     boshqa toifa umuman ko'rinmaydi: xato yo'lni ko'rsatib, keyin uni rad
     etishdan ko'ra, uni umuman taklif qilmaslik yaxshiroq. */
  const needsWarehouse = role !== null && roleNeedsWarehouse(role.permissions);

  /* Tanlash mumkin bo'lgan korxonalar — administratorning O'Z doirasidan.
     Ro'yxat doira tanlagichi bilan bir manbadan keladi, shuning uchun
     server rad etadigan korxona bu yerda umuman ko'rinmaydi. */
  const workplaceUnits = workplaceUnitsOf(scopeOptions);

  const pickRole = (nextId: string): void => {
    setRoleId(nextId);
    const next = roles.find((item) => item.id === nextId);
    if (next !== undefined) setScope((current) => scopeForRole(next.permissions, current));
  };

  const digits = jshshir.replace(/\D/g, '').slice(0, JSHSHIR_LENGTH);
  const canSearch = digits.length === JSHSHIR_LENGTH && !lookup.isPending;
  /* Kadrlar bazasida ish joyi yo'q — uni administrator ko'rsatadi.
     Prodda bunday yozuv 63 470 dan 37 471 ta, ya'ni bu istisno emas. */
  const needsWorkplace = manual || (found !== null && found.orgUnitId === null);
  const hasPosition = position.id !== null || position.name !== null;
  const workplaceReady = !needsWorkplace || (orgUnitId !== '' && hasPosition);
  const nameReady = !manual || fullName.trim().length >= 3;
  const canCreate =
    (found !== null || manual) &&
    found?.userActive !== true &&
    nameReady &&
    roleId !== '' &&
    workplaceReady &&
    (scope.type === 'GLOBAL' || scope.id !== null);

  /* Hook dan faqat nusxalash olinadi: belgi bu yerda oyna yopilguncha
     turishi kerak (pastda `setCopied(false)` bilan tozalanadi). */
  const copy = (value: string): void => {
    void copyText(value).then((ok) => {
      if (ok) setCopied(true);
    });
  };

  return (
    // Oyna ichidagi barcha tanlagichlar yangilangan ko'rinishda chiziladi.
    <FieldAppearanceContext.Provider value="refined">
      <Dialog
        open={open}
        onClose={onClose}
        title={t('staff.addTitle')}
        footer={
          result !== null ? (
            <Button onClick={onClose}>{t('staff.addDone')}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                {t('staff.addCancel')}
              </Button>
              <Button
                loading={create.isPending}
                disabled={!canCreate}
                onClick={() => create.mutate()}
              >
                <UserPlus size={16} aria-hidden />
                {t('staff.addConfirm')}
              </Button>
            </>
          )
        }
      >
        {result !== null ? (
          <CreatedPanel result={result} copied={copied} onCopy={copy} />
        ) : (
          <div className="flex flex-col gap-4">
            {/* 1-qadam: qidiruv. Topilgandan keyin ham ko'rinib turadi —
                administrator raqamni xato tergan bo'lsa, tuzatib qayta qidiradi. */}
            <div className="flex items-end gap-2">
              <Input
                id={jshshirId}
                label={t('staff.addJshshir')}
                mono
                inputMode="numeric"
                autoComplete="off"
                className="flex-1"
                value={digits}
                placeholder={t('staff.addJshshirPlaceholder')}
                onChange={(event) => {
                  setJshshir(event.target.value);
                  // Raqam o'zgardi — eski natija endi boshqa odamniki.
                  setFound(null);
                  setManual(false);
                  lookup.reset();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && canSearch) lookup.mutate(digits);
                }}
                error={
                  lookup.error === null || lookup.error === undefined
                    ? undefined
                    : errorText(lookup.error, t('staff.addLookupFailed'))
                }
              />
              <Button
                variant="secondary"
                disabled={!canSearch}
                loading={lookup.isPending}
                onClick={() => lookup.mutate(digits)}
              >
                <Search size={16} aria-hidden />
                {t('staff.addSearch')}
              </Button>
            </div>

            {found === null && !manual ? (
              <>
                <p className="text-text-muted text-sm text-pretty">{t('staff.addHint')}</p>

                {/*
                  KADRLAR BAZASIDA YO'Q XODIM.
                  Taklif faqat qidiruv «topilmadi» degandan KEYIN chiqadi:
                  qo'lda kiritish istisno bo'lib qolishi kerak, birinchi
                  yo'l emas. Aks holda administrator qidirmasdan teraverardi
                  va bazadagi rasmiy ma'lumot chetda qolardi.
                */}
                {lookup.isError && digits.length === JSHSHIR_LENGTH ? (
                  <div className="border-border bg-surface-2 flex flex-col gap-2 rounded-[var(--radius-sm)] border px-3 py-3">
                    <p className="text-text-muted text-sm text-pretty">
                      {t('staff.addManualHint')}
                    </p>
                    <div>
                      <Button variant="secondary" size="sm" onClick={() => setManual(true)}>
                        <UserPlus size={15} aria-hidden />
                        {t('staff.addManualStart')}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                {/* Topilgan odam — KARTOCHKA; qo'lda kiritilayotgani —
                    TAHRIRLANADIGAN maydonlar. Qolgan hamma narsa (ish joyi,
                    bo'lim, rol, doira) ikkala yo'lda ham bir xil. */}
                {found === null ? (
                  <div className="border-warn/30 bg-warn-soft flex flex-col gap-3 rounded-[var(--radius-sm)] border px-3 py-3">
                    <p className="text-text flex items-start gap-2 text-sm">
                      <AlertTriangle size={16} aria-hidden className="text-warn mt-0.5 shrink-0" />
                      <span className="text-pretty">{t('staff.addManualNotice')}</span>
                    </p>

                    <Input
                      label={t('staff.addManualFullName')}
                      placeholder={t('staff.addManualFullNamePlaceholder')}
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                    />
                    <Input
                      label={t('staff.addManualPhone')}
                      placeholder={t('staff.addManualPhonePlaceholder')}
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                    />
                  </div>
                ) : (
                  <FoundCard found={found} />
                )}

                {found?.userActive === true ? (
                  <p
                    role="alert"
                    className="border-warn/30 bg-warn-soft text-warn flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
                  >
                    <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
                    <span className="text-pretty">{t('staff.addAlreadyUser')}</span>
                  </p>
                ) : (
                  <>
                    {found === null || found.userId === null ? null : (
                      <p className="border-accent/25 bg-accent-soft text-text flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-sm">
                        <RotateCcw size={16} aria-hidden className="text-accent mt-0.5 shrink-0" />
                        <span className="text-pretty">{t('staff.addReactivates')}</span>
                      </p>
                    )}

                    {!needsWorkplace ? null : (
                      <div className="border-warn/30 bg-warn-soft flex flex-col gap-3 rounded-[var(--radius-sm)] border px-3 py-3">
                        <p className="text-text flex items-start gap-2 text-sm">
                          <AlertTriangle size={16} aria-hidden className="text-warn mt-0.5 shrink-0" />
                          <span className="text-pretty">{t('staff.addNoWorkplace')}</span>
                        </p>

                        <Select
                          label={t('staff.addWorkplaceOrgUnit')}
                          placeholder={t('staff.addWorkplaceOrgUnitPlaceholder')}
                          value={orgUnitId}
                          options={workplaceUnits.map((unit) => ({
                            value: unit.id,
                            label: unit.label,
                            hint: unit.hint,
                          }))}
                          onChange={(event) => {
                            const next = event.target.value;
                            setOrgUnitId(next);
                            /* Rol doirasi ham shu korxonaga qo'yiladi: xodim
                               qayerda ishlasa, roli ham o'sha yerda bo'ladi. */
                            setScope((current) =>
                              current.type === 'ORG_UNIT' ? { type: 'ORG_UNIT', id: next } : current,
                            );
                          }}
                        />

                        <PositionField
                          label={t('staff.addWorkplacePosition')}
                          placeholder={t('staff.addWorkplacePositionPlaceholder')}
                          value={position}
                          onChange={setPosition}
                        />
                      </div>
                    )}

                    {/* Bo'linma — ish joyi ma'lum bo'lgandagina va faqat o'sha
                        korxonaning bo'limlari orasidan. Ro'yxat bo'sh bo'lsa
                        maydon umuman chizilmaydi: tanlash uchun hech narsa yo'q
                        bo'lgan bo'sh tanlagich savol tug'diradi, javob bermaydi. */}
                    {workplaceOrgUnitId === null ||
                    (subdivisions.data?.items ?? []).length === 0 ? null : (
                      <Select
                        label={t('staff.fieldSubdivision')}
                        placeholder={t('staff.subdivisionNone')}
                        value={subdivisionId}
                        options={(subdivisions.data?.items ?? []).map((row) => ({
                          value: row.id,
                          label: row.name,
                        }))}
                        onChange={(event) => setSubdivisionId(event.target.value)}
                      />
                    )}

                    <Select
                      label={t('staff.addRole')}
                      placeholder={t('staff.addRolePlaceholder')}
                      value={roleId}
                      options={roles.map((role) => ({ value: role.id, label: role.name }))}
                      onChange={(event) => pickRole(event.target.value)}
                    />

                    <ScopePicker
                      value={scope}
                      onChange={setScope}
                      options={scopeOptions}
                      {...(needsWarehouse ? { requiredKind: 'WAREHOUSE' as const } : {})}
                    />

                    {create.error === null || create.error === undefined ? null : (
                      <p
                        role="alert"
                        className="border-danger/30 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
                      >
                        {errorText(create.error, t('staff.addFailed'))}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </Dialog>
    </FieldAppearanceContext.Provider>
  );
};

/** Kadrlar bazasidan topilgan xodim — o'zgartirib bo'lmaydigan ma'lumot. */
const FoundCard = ({ found }: { found: PersonnelLookupDto }) => (
  <dl className="border-border bg-surface-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-[var(--radius-sm)] border px-3 py-3 text-sm">
    <dt className="text-text-muted">{t('staff.addFoundName')}</dt>
    <dd className="text-text font-medium">{found.fullName}</dd>

    <dt className="text-text-muted">{t('staff.addFoundOrgUnit')}</dt>
    <dd className="text-text">{found.orgUnitName ?? t('staff.addFoundNone')}</dd>

    <dt className="text-text-muted">{t('staff.addFoundPosition')}</dt>
    <dd className="text-text">{found.positionName ?? t('staff.addFoundNone')}</dd>

    <dt className="text-text-muted">{t('staff.addFoundPhone')}</dt>
    <dd className="tabular text-text">{found.phone ?? t('staff.addFoundNone')}</dd>
  </dl>
);

/**
 * Natija — parol tiklash oynasidagi bilan bir xil ko'rinishda: ikki joyda
 * boshqa-boshqa chizilsa, administrator ekranni har safar qaytadan o'qirdi.
 */
const CreatedPanel = ({
  result,
  copied,
  onCopy,
}: {
  result: CreateStaffResultDto;
  copied: boolean;
  onCopy: (value: string) => void;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <span className="text-text font-medium">{result.fullName}</span>
      <span className="tabular text-text-muted text-sm">{result.username}</span>
    </div>

    {/* Parol HAR DOIM ekranda: Telegram — qo'shimcha nusxa, yagona yo'l emas. */}
    <div className="flex flex-col gap-3">
        <p className="border-warn/30 bg-warn-soft text-warn flex items-start gap-2 rounded-[var(--radius-sm)] border px-3 py-2 text-sm">
          <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
          <span className="text-pretty">{t('staff.addOnScreen')}</span>
        </p>

        <div
          className={cn(
            'border-border bg-surface-2 flex items-center justify-between gap-3',
            'rounded-[var(--radius-sm)] border px-3 py-2.5',
          )}
        >
          <code className="tabular text-text text-base font-semibold tracking-wide">
            {result.password}
          </code>
          <Button variant="secondary" size="sm" onClick={() => onCopy(result.password)}>
            {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            {copied ? t('staff.resetCopied') : t('staff.resetCopy')}
          </Button>
        </div>

      <Badge tone="warn">{t('staff.resetOnce')}</Badge>

      {result.delivery === 'telegram' ? (
        <p className="text-text-muted flex items-start gap-2 text-sm">
          <Send size={16} aria-hidden className="text-accent mt-0.5 shrink-0" />
          <span className="text-pretty">{t('staff.addSentTelegram')}</span>
        </p>
      ) : null}
    </div>
  </div>
);
